import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Skin } from '@core/interfaces/skin.interface';
import { SessionService } from '@core/services/session.service';
import { sleep, sleepWithId } from '@core/utils/sleep.util';
import { generateSeededRandomNumber } from '@core/utils/seeded-random.util';
import { transition, animate, style, trigger } from '@angular/animations';
import { ICase, IWinSkin } from '@features/cases/interfaces/cases.interface';
import { CasesService } from '@features/cases/services/cases.service';
import { ICaseBattle } from '../interfaces/case-battles.interface';
import { CaseBattlesService } from '../case-battles.service';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { SoundsService } from '@core/services/sounds.service';

@Component({
  selector: 'app-cb-battle-spinner-2',
  templateUrl: './cb-battle-spinner-2.component.html',
  styleUrl: './cb-battle-spinner-2.component.scss',
  standalone: true,
  imports: [CostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('scaleIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '0.2s ease-in-out',
          style({ opacity: 1, transform: 'scale(1)' })
        ),
      ]),
    ]),
  ],
})
export class CbBattleSpinner2Component {
  private soundsService = inject(SoundsService);
  public sessionService = inject(SessionService);
  private casesService = inject(CasesService);
  private caseBattlesService = inject(CaseBattlesService);

  private destroyRef = inject(DestroyRef);

  battle = input.required<ICaseBattle>();
  slot = input<number>();
  playSound = input<boolean>();

  currentCase = computed<ICase | null>(() => {
    const c = this.casesService.findCaseByLink(
      this.battle().cases[this.battle().rounds.length - 1].link
    );
    // console.log(c, '<<< currentCase c');
    return c || null;
  });

  player = computed(() => {
    return (
      this.battle().participants.find((p) => p.slot === this.slot()) ?? null
    );
  });

  spinnerItems = signal<Skin[]>([]);
  showResults = signal(false);

  settings = computed(() => {
    return this.caseBattlesService.getBattleSettings(this.battle());
  });
  wonIndex = signal<number>(0);

  rollElement = viewChild.required('roll', { read: ElementRef });
  containerElement = viewChild.required('container', { read: ElementRef });

  private animationFrameId: number | null = null;

  timeoutIds = signal<number[]>([]);

  constructor() {
    const replayEffect = effect(() => {
      if (this.battle()._replay) {
        this.resetTimeoutIds();
        this.stopAnimation();
        replayEffect.destroy();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.stopAnimation();
      this.resetTimeoutIds();
    });
  }

  private resetRoll(sizeCoefficient: number) {
    this.showResults.set(false);
    this.rollElement().nativeElement.style.transition = `none`;
    this.rollElement().nativeElement.style.transform = `translateY(-${
      sizeCoefficient * 5 + sizeCoefficient / 2 + 0.1
    }%)`;

    this.rollElement()
      .nativeElement.querySelectorAll('.win')
      .forEach((el: any) => el.classList.remove('win'));
    this.rollElement()
      .nativeElement.querySelectorAll('.lose')
      .forEach((el: any) => el.classList.remove('lose'));
  }

  private randomizeSpinnerItems(winItem: IWinSkin) {
    const currentCase = this.currentCase();
    if (!currentCase) return;

    const { ITEMS_QUANTITY, WIN_INDEX } = this.settings();

    const items: Skin[] = [];
    while (items.length < ITEMS_QUANTITY) {
      const itemModifiedColor =
        currentCase.skins[Math.floor(Math.random() * currentCase.skins.length)];
      items.push(itemModifiedColor);
    }
    // console.log(winItem, '<<< winItem');
    const winSkin = currentCase.skins.find((item) => {
      return item.name.toLowerCase() === winItem.skin.toLowerCase();
    });
    // console.log(winSkin, '<<< winSkin');

    if (winSkin) {
      items[WIN_INDEX] = { ...winSkin };
    } else {
      console.warn(
        `Win skin ${winItem.skin} not found in current case ${currentCase.link}`
      );
    }

    this.spinnerItems.set(items);
  }

  private toggleStyles(type: 'remove' | 'add' = 'add') {
    const children = Array.from(
      this.rollElement().nativeElement?.children ?? []
    );
    for (let i = 0; i < children.length; i++) {
      const child: HTMLElement | null = children[i] as HTMLElement | null;

      if (type === 'add') {
        child?.classList?.add(i === this.settings().WIN_INDEX ? 'win' : 'lose');
      } else {
        child?.classList?.remove('win');
        child?.classList?.remove('lose');
        child?.classList?.remove('active');
      }
    }
  }

  private startAnimation(size: number) {
    const animate = () => {
      const containerRect =
        this.containerElement().nativeElement.getBoundingClientRect();
      const rollRect = this.rollElement().nativeElement.getBoundingClientRect();
      const rollCenter = containerRect.top + containerRect.height / 2;

      const transformPercentage =
        (Math.abs(rollRect.top - rollCenter) / rollRect.height) * 100;
      const currentRoll = Math.floor(transformPercentage / size);

      if (
        currentRoll >= 0 &&
        currentRoll < this.rollElement().nativeElement.children.length
      ) {
        this.updateActiveItem(currentRoll);
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private stopAnimation() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private updateActiveItem(index: number) {
    const rollElement = this.rollElement().nativeElement;
    const currentActive = rollElement.querySelector('.active');
    const newActive = rollElement.children[index];
    if (currentActive !== newActive) {
      if (currentActive) {
        currentActive.classList.remove('active');
      }
      if (newActive && !newActive.classList.contains('active')) {
        newActive.classList.add('active');
        if (this.playSound()) {
          this.soundsService.playSound('tick', {
            extension: 'mp3',
            maxVolume: 0.2,
          });
        }
      }
    }
  }

  public async roll(winItem: IWinSkin, allWinItems: IWinSkin[] = []) {
    this.toggleStyles('remove');
    this.showResults.set(false);

    const {
      ITEMS_QUANTITY,
      ROLLING_CLOSER_TIMEOUT,
      ROLLING_TIMEOUT,
      ROUND_RESULTS_TIMEOUT,
      WIN_INDEX,
    } = this.settings();
    this.wonIndex.set(WIN_INDEX);
    const sizeCoefficient = 100 / ITEMS_QUANTITY;

    this.resetRoll(sizeCoefficient);
    this.randomizeSpinnerItems(winItem);

    await sleep(50);

    const GAP_SIZE_PERCENTAGE = 0.01;
    const endPositionPercentage =
      (await generateSeededRandomNumber(
        `${winItem.chance}chance${winItem.multiplier}`,
        0,
        100
      ).then((result) => result)) / 100;

    const middleLocation = -(sizeCoefficient * WIN_INDEX) - sizeCoefficient / 2;

    const transformY =
      -(sizeCoefficient * WIN_INDEX) -
      (sizeCoefficient - GAP_SIZE_PERCENTAGE * 2) * endPositionPercentage -
      GAP_SIZE_PERCENTAGE;
    this.rollElement().nativeElement.style.top = `50%`;

    this.rollElement().nativeElement.style.transition = `transform ${
      ROLLING_TIMEOUT / 1000
    }s ${this.settings().BEZIER_FUNCTION}`;
    this.rollElement().nativeElement.style.transform = `translateY(${transformY}%)`;

    this.startAnimation(sizeCoefficient);
    const { promise, timeoutId } = await sleepWithId(ROLLING_TIMEOUT);
    if (timeoutId) {
      this.timeoutIds.update((prev) => [...prev, timeoutId]);
    }
    await promise;
    this.stopAnimation();
    const { promise: promise2, timeoutId: timeoutId2 } = await sleepWithId(
      ROLLING_CLOSER_TIMEOUT
    );
    if (timeoutId2) {
      this.timeoutIds.update((prev) => [...prev, timeoutId2]);
    }
    await promise2;

    if (this.playSound()) {
      this.soundsService.playSound('tick', {
        extension: 'mp3',
        maxVolume: 0.2,
      });
    }

    this.rollElement().nativeElement.style.transition = `transform ${
      ROLLING_CLOSER_TIMEOUT / 1000
    }s linear`;
    this.rollElement().nativeElement.style.transform = `translateY(${middleLocation}%)`;
    this.showResults.set(true);
    this.toggleStyles('add');

    if (this.soundsService.volume() && this.playSound()) {
      const prices = allWinItems.map((item) => item.price);
      const highestPrice = prices.reduce((a, b) => (a > b ? a : b), 0);
      const multiplier = highestPrice / (this.currentCase()?.price ?? 0);
      if (multiplier > 25) {
        this.soundsService.playSound('lightning', { maxVolume: 0.1 });
      } else if (multiplier > 10) {
        this.soundsService.playSound('plinko-high', {
          maxVolume: 0.6,
          extension: 'mp3',
        });
      } else if (multiplier > 3) {
        this.soundsService.playSound('plinko-med', {
          maxVolume: 0.8,
          extension: 'mp3',
        });
      } else {
        this.soundsService.playSound('plinko-low', {
          maxVolume: 0.8,
          extension: 'mp3',
        });
      }
    }

    const { promise: promise3, timeoutId: timeoutId3 } = await sleepWithId(
      ROUND_RESULTS_TIMEOUT
    );
    if (timeoutId3) {
      this.timeoutIds.update((prev) => [...prev, timeoutId3]);
    }
    await promise3;
  }

  public async rollWithoutAnimation(winItem: IWinSkin) {
    const { ITEMS_QUANTITY, WIN_INDEX } = this.settings();
    const sizeCoefficient = 100 / ITEMS_QUANTITY;

    const middleLocation = -(sizeCoefficient * WIN_INDEX) - sizeCoefficient / 2;
    this.rollElement().nativeElement.style.top = `50%`;
    this.rollElement().nativeElement.style.transition = `transform none`;
    this.rollElement().nativeElement.style.transform = `translateY(${middleLocation}%)`;

    this.wonIndex.set(WIN_INDEX);
    this.showResults.set(true);

    this.randomizeSpinnerItems(winItem);
    await sleep(20);
    this.toggleStyles('add');
  }

  public resetTimeoutIds() {
    this.timeoutIds().forEach((id) => {
      clearTimeout(id);
    });
    this.timeoutIds.set([]);
  }
}
